import { useToast } from '@kcd/design-system';

import Image from '~/components/Image';
import NonLoginECouponProductPurchaseButton from '~/components/NonLogin/NonLoginECouponProductPurchaseButton';
import { E_COUPON_CHECKOUT } from '~/constants/checkout/variables';
import { useCurrentUser } from '~/domains/auth/queries';
import { useUpdateECoponQuick } from '~/domains/cart/queries';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import { usePushWithQuery } from '~/hooks/url';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { UpdateECouponQuickReqParams } from '~/pages/vendors/[vendorId]/products/[offerId]/components/ECoupon/ECouponOrderButton';
import type { OAISchema } from '~/types/openapi';

import ECouponProductPurchaseButton from '../components/ECouponProductPurchaseButton';
import ProductPrice from '../components/ProductPrice';

import PointOnly12Icon from '~/assets/icons/point_only_12x12.svg';

import styles from './ECouponProductDisplayItem.module.css';

type Props = {
  product: OAISchema['MarketSelectProductResponseV2'];
  eventOption?: Record<string, unknown>;
  reverseCouponDiscountedPrice?: boolean;
};

const ECouponProductDisplayItem = ({
  product,
  eventOption,
  reverseCouponDiscountedPrice,
}: Props) => {
  const { isLoggedIn } = useCurrentUser();
  const toast = useToast();
  const nextPush = usePushWithQuery();
  const updateQuickECoupon = useUpdateECoponQuick();
  const [{ onClick }] = useTrackProductEventProps(product, eventOption);
  const LinkComponent = useLinkComponent();

  const onClickProduct = () => {
    onClick?.();
  };

  const onClickPurchase = async () => {
    const requestBody: UpdateECouponQuickReqParams = {
      offerId: product.offerId!,
    };

    try {
      await updateQuickECoupon({
        requestBody,
      });
      void nextPush(E_COUPON_CHECKOUT, false);
    } catch (error) {
      if (error instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(error);
        toast(errMessage, {
          offset: 86,
        });
      }
      throw error;
    }
  };

  return (
    <div className={styles.wrap}>
      <div
        className={styles.mainImage}
        role="presentation"
        onClick={onClickProduct}
      >
        <LinkComponent
          href={`/vendors/${product.vendorId}/products/${product.offerId!}`}
        >
          <Image width={92} height={92} alt="" src={product.mainImages![0]} />
        </LinkComponent>
      </div>
      <div
        role="presentation"
        onClick={onClickProduct}
        className={styles.productInfo}
      >
        <LinkComponent
          href={`/vendors/${product.vendorId}/products/${product.offerId!}`}
        >
          <div>
            {product.eCouponInfo?.eCouponProductType === 'FREE_POINT_ONLY' && (
              <span className="mb-2 flex items-center gap-4 text-blue-600 typo-footnote300-semibold">
                <PointOnly12Icon />
                포인트 전용 상품
              </span>
            )}
            <p className={styles.productName}>{product.productName}</p>
            <ProductPrice {...product} reverse={reverseCouponDiscountedPrice} />
          </div>
        </LinkComponent>
      </div>
      {isLoggedIn ? (
        <ECouponProductPurchaseButton
          className="!mr-0 shrink-0"
          product={product}
          onClickHandler={onClickPurchase}
          ctaText={product.eCouponOrderDisabledLabel}
          disabled={product.eCouponOrderDisabled}
        />
      ) : (
        <NonLoginECouponProductPurchaseButton />
      )}
    </div>
  );
};

export default ECouponProductDisplayItem;
